// Card provider in cima alla pagina Routing AI: provider nativi ed endpoint
// Custom AI nella stessa riga, card compatte che si dividono tutta la
// larghezza (3 o 4 che siano; a capo solo su schermi stretti). Sola lettura:
// stato e numero di funzioni che usano il provider. Chiavi ed endpoint si
// gestiscono nella scheda Provider AI.
import type { LucideIcon } from 'lucide-react';
import type { KpiTone } from '@/pages/logistics/riepilogo/shared';

export type ProviderOverviewStatus =
  | 'active'
  | 'disabled'
  | 'not_configured'
  | 'degraded'
  | 'down'
  | 'excluded'
  | 'unverified';

export interface ProviderOverviewEntry {
  /** 'openai' | 'claude' | 'google' | 'custom:<uuid>' */
  id: string;
  /** Titolo della card (nome del provider, oppure "Custom AI"). */
  name: string;
  /** Riga sotto il titolo (es. "Integrazione nativa", nome dell'endpoint Custom AI). */
  detail: string;
  status: ProviderOverviewStatus;
  /** Funzioni di questa pagina che usano il provider. */
  usage: number;
  icon: LucideIcon;
  tone: KpiTone;
}

interface ProviderOverviewProps {
  entries: ReadonlyArray<ProviderOverviewEntry>;
}

const STATUS_META: Record<ProviderOverviewStatus, { label: string; className: string }> = {
  active: { label: 'Attivo', className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20' },
  disabled: { label: 'Disattivato', className: 'bg-muted text-muted-foreground border-border' },
  not_configured: { label: 'Non configurato', className: 'bg-muted text-muted-foreground border-border' },
  degraded: { label: 'Degradato', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' },
  down: { label: 'Non raggiungibile', className: 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20' },
  excluded: { label: 'Escluso dal routing', className: 'bg-muted text-muted-foreground border-border' },
  unverified: { label: 'Da verificare', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' },
};

function usageLabel(usage: number): string {
  if (usage === 0) return 'Non usato';
  return `Usato da ${usage} ${usage === 1 ? 'funzione' : 'funzioni'}`;
}

const TONE_ICON: Record<KpiTone, string> = {
  indigo: 'bg-indigo-500/15 text-indigo-500 dark:text-indigo-400',
  violet: 'bg-violet-500/15 text-violet-500 dark:text-violet-400',
  emerald: 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-400',
  cyan: 'bg-cyan-500/15 text-cyan-500 dark:text-cyan-400',
  amber: 'bg-amber-500/15 text-amber-500 dark:text-amber-400',
  rose: 'bg-rose-500/15 text-rose-500 dark:text-rose-400',
  blue: 'bg-blue-500/15 text-blue-500 dark:text-blue-400',
  orange: 'bg-orange-500/15 text-orange-500 dark:text-orange-400',
};

export default function ProviderOverview({ entries }: ProviderOverviewProps) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="provider-overview">
      {entries.map((entry) => {
        const status = STATUS_META[entry.status];
        const Icon = entry.icon;
        return (
          <div
            key={entry.id}
            className="flex min-w-[11rem] flex-1 basis-40 items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
            data-testid={`provider-card-${entry.id}`}
            data-provider-status={entry.status}
          >
            <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${TONE_ICON[entry.tone]}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold text-foreground">{entry.name}</span>
                <span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[10px] font-medium ${status.className}`}>
                  {status.label}
                </span>
              </div>
              <p className="truncate text-[11px] text-muted-foreground">{entry.detail}</p>
              <p
                className={`text-[11px] ${entry.usage > 0 ? 'text-foreground' : 'text-muted-foreground'}`}
                data-testid={`provider-usage-${entry.id}`}
              >
                {usageLabel(entry.usage)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
